"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileSpreadsheet, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface WedgeHeroCopy {
  categoryBadge: string;
  heading: string;
  lead: string;
  secondaryCta: string;
  chipLabel: string;
  footerLeft: string;
  footerRight: string;
  specStrip: Array<{
    label: string;
    value: string;
  }>;
}
const copyData: Record<Lang, WedgeHeroCopy> = {
  en: {
    categoryBadge: "Wedge Wire Screen Specifications",
    heading: "Wedge wire screens engineered to exact slot specifications",
    lead: "Define slot openings from 0.02 to 5.0 mm, profile wire geometry, and support rod spacing for continuous, non-clogging industrial separation. Resistance-welded in stainless steel alloys with verified mechanical tolerances for demanding differential pressures.",
    secondaryCta: "View selection guide",
    chipLabel: "V-Wire Profile Geometry",
    footerLeft: "Flat panels, cylinders, curved & sieve bends",
    footerRight: "All screen types",
    specStrip: [{
      label: "Slot Opening Range",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Profile Wire Geometry",
      value: "V-Shape / Vee-Wire"
    }, {
      label: "Alloy Metallurgy",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "Flow Configurations",
      value: "FOTI & FITO Continuous"
    }]
  },
  zh: {
    categoryBadge: "楔形丝筛网技术规格",
    heading: "按精准缝隙规格定制的高精度楔形丝筛网",
    lead: "支持 0.02 至 5.0 mm 连续缝隙开孔、V型丝截面几何与支撑筋间距定制。采用不锈钢合金全电阻焊制造，提供严格的机械尺寸公差，满足工业固液分离的高压差与防堵塞要求。",
    secondaryCta: "查看选型指南",
    chipLabel: "V型丝截面几何构造",
    footerLeft: "平板筛板、圆柱滤筒、弧形筛板",
    footerRight: "全部筛网类型",
    specStrip: [{
      label: "缝隙开孔范围",
      value: "0.02 – 5.0 mm"
    }, {
      label: "型丝几何形状",
      value: "V型截面 / Vee-Wire"
    }, {
      label: "合金材质",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "流向结构",
      value: "FOTI / FITO 连续缝隙"
    }]
  },
  es: {
    categoryBadge: "Especificaciones de mallas en cuña",
    heading: "Mallas de alambre en cuña diseñadas según especificaciones exactas de ranura",
    lead: "Defina aberturas de ranura de 0,02 a 5,0 mm, geometría de alambre perfilado y espaciado de varillas de soporte para separación industrial continua sin obstrucciones. Soldadura por resistencia en aceros inoxidables para altas presiones diferenciales.",
    secondaryCta: "Ver guía de selección",
    chipLabel: "Geometría de alambre en V",
    footerLeft: "Paneles planos, cilindros y tamices curvados",
    footerRight: "Todos los tipos",
    specStrip: [{
      label: "Rango de abertura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Geometría de alambre",
      value: "Perfil en V / Vee-Wire"
    }, {
      label: "Aleaciones",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "Configuración de flujo",
      value: "FOTI y FITO continuo"
    }]
  },
  de: {
    categoryBadge: "Spaltsieb-Spezifikationen",
    heading: "Spaltsiebe präzise nach exakten Spaltweiten gefertigt",
    lead: "Definieren Sie Spaltweiten von 0,02 bis 5,0 mm, Profildrahtgeometrien und Tragstababstände für verstopfungsfreie kontinuierliche Fest-Flüssig-Trennung. Widerstandsgeschweißt aus Edelstahllegierungen für anspruchsvolle Differenzdrücke.",
    secondaryCta: "Auswahlleitfaden ansehen",
    chipLabel: "V-Profildraht-Geometrie",
    footerLeft: "Flachpaneele, Zylinder und Bogensiebe",
    footerRight: "Alle Spaltsiebe",
    specStrip: [{
      label: "Spaltweitenbereich",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Profildrahtgeometrie",
      value: "V-Profil / Vee-Wire"
    }, {
      label: "Legierungen",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "Strömungsrichtung",
      value: "FOTI & FITO kontinuierlich"
    }]
  },
  fr: {
    categoryBadge: "Spécifications des grilles à fil profilé",
    heading: "Grilles à fil profilé fabriquées selon vos spécifications de fente exactes",
    lead: "Définissez des ouvertures de fente de 0,02 à 5,0 mm, des profils de fil en V et des espacements de supports pour une séparation solide-liquide continue anti-colmatage. Soudées par résistance en acier inoxydable pour résister aux fortes pressions différentielles.",
    secondaryCta: "Voir le guide de sélection",
    chipLabel: "Géométrie du fil en V",
    footerLeft: "Panneaux plats, cylindres et grilles courbes",
    footerRight: "Tous les types",
    specStrip: [{
      label: "Plage de fentes",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Géométrie du profil",
      value: "Fil en V / Vee-Wire"
    }, {
      label: "Alliages métalliques",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "Sens d'écoulement",
      value: "FOTI & FITO continu"
    }]
  },
  pt: {
    categoryBadge: "Especificações de telas tipo cunha",
    heading: "Telas de arame em cunha projetadas para suas especificações exatas de fenda",
    lead: "Defina aberturas de fenda de 0,02 a 5,0 mm, geometria de arame perfilado e espaçamento de hastes para separação contínua e sem entupimento. Soldadas por resistência em aço inoxidável para suportar severas pressões diferenciais.",
    secondaryCta: "Ver guia de seleção",
    chipLabel: "Geometria do perfil em V",
    footerLeft: "Painéis planos, cilindros e telas curvas",
    footerRight: "Todos os modelos",
    specStrip: [{
      label: "Faixa de abertura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Geometria do perfil",
      value: "Perfil em V / Vee-Wire"
    }, {
      label: "Ligas metálicas",
      value: "AISI 304, 316L, 321, 2205"
    }, {
      label: "Sentido de fluxo",
      value: "FOTI e FITO contínuo"
    }]
  }
};
export default function WedgeSpecsHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="wedgewirespecshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges Row - Exactly two badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Single Page H1 */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.heading}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/WedgeWireScreenSelectionGuidePage" className="inline-flex items-center gap-2">
                  <FileSpreadsheet className="size-4" aria-hidden="true" />
                  <span>{copy.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Card (5 cols on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedgewire_profile_detail.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedgewire_profile_detail.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedgewire_profile_detail.png-wvc-sizes" alt="Wedge wire profile and slot opening diagram showing continuous slot geometry" loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.chipLabel}</span>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{copy.footerLeft}</span>
                  </div>
                  <Link to="/WedgeWireScreenPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.footerRight}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {copy.specStrip.map((item, index) => <div key={index} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{item.label}</span>
              <span className="mf-spec-strip__value">{item.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}